import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { callQuery, GET_SYSTEM_BANNER } from '../../amplify/appsync/query';
import { callWrite } from '../../amplify/appsync/write';
import { UPDATE_SYSTEM_BANNER } from '../../amplify/appsync/write';

interface SystemBannerData {
    id: string;
    message?: string;
    variant?: 'info' | 'warning' | 'danger';
    isActive?: boolean;
    updatedAt?: number;
}

export const SystemBannerAdmin: React.FC = () => {
    const [banner, setBanner] = useState<SystemBannerData>({
        id: 'active',
        message: '',
        variant: 'info',
        isActive: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        fetchBanner();
    }, []);

    const fetchBanner = async () => {
        setIsLoading(true);
        try {
            const response = await callQuery({
                operation: GET_SYSTEM_BANNER,
            });

            if (response && response.id) {
                setBanner(response);
            }
        } catch (error) {
            console.error('Error fetching system banner:', error);
            setSaveMessage({ type: 'error', text: 'Failed to load banner configuration' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            await callWrite({
                operation: UPDATE_SYSTEM_BANNER,
                banner: {
                    message: banner.message,
                    variant: banner.variant,
                    isActive: banner.isActive,
                },
            });

            setSaveMessage({ type: 'success', text: 'Banner updated successfully!' });

            // Refresh banner data to get the updated timestamp
            await fetchBanner();
        } catch (error: any) {
            console.error('Error updating system banner:', error);
            const errorMessage = error?.message || 'Failed to update banner';
            setSaveMessage({ type: 'error', text: errorMessage });
        } finally {
            setIsSaving(false);
        }
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBanner({ ...banner, message: e.target.value });
    };

    const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setBanner({ ...banner, variant: e.target.value as 'info' | 'warning' | 'danger' });
    };

    const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBanner({ ...banner, isActive: e.target.checked });
    };

    if (isLoading) {
        return (
            <Container className="mt-4">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    return (
        <Container style={{marginTop: '80px'}}>
            <Row>
                <Col lg={8} className="mx-auto">
                    <h2 className="mb-4">System Banner Management</h2>

                    {saveMessage && (
                        <Alert
                            variant={saveMessage.type === 'success' ? 'success' : 'danger'}
                            dismissible
                            onClose={() => setSaveMessage(null)}
                        >
                            {saveMessage.text}
                        </Alert>
                    )}

                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Banner Configuration</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={banner.message || ''}
                                        onChange={handleMessageChange}
                                        placeholder="Enter the banner message..."
                                    />
                                    <Form.Text className="text-muted">
                                        This message will be displayed to all users when the banner is active.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Severity</Form.Label>
                                    <Form.Select
                                        value={banner.variant || 'info'}
                                        onChange={handleVariantChange}
                                    >
                                        <option value="info">Info (Blue)</option>
                                        <option value="warning">Warning (Yellow)</option>
                                        <option value="danger">Critical (Red)</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Choose the appropriate severity level for the banner.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="active-switch"
                                        label="Active"
                                        checked={banner.isActive || false}
                                        onChange={handleActiveChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Toggle to show or hide the banner. When disabled, the banner will not be displayed to users.
                                    </Form.Text>
                                </Form.Group>

                                <div className="d-grid gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {banner.message && (
                        <Card>
                            <Card.Header>
                                <h5 className="mb-0">Preview</h5>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {banner.isActive ? (
                                    <Alert variant={banner.variant || 'info'} className="mb-0 rounded-0">
                                        <div className="text-center fw-medium">
                                            {banner.message}
                                        </div>
                                    </Alert>
                                ) : (
                                    <div className="p-3 text-muted text-center">
                                        Banner is currently inactive. Enable it above to display to users.
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
};
